import {
  AcademicCapIcon,
  BookOpenIcon,
  BriefcaseIcon,
  CheckBadgeIcon,
  ClipboardDocumentCheckIcon,
  Cog6ToothIcon,
  DocumentCheckIcon,
  DocumentIcon,
  HomeIcon,
  IdentificationIcon,
  PencilSquareIcon,
  ScaleIcon,
  ShieldCheckIcon,
  TagIcon,
  UserGroupIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { IconType } from '~/types';
import { PROVIDER_PAGE_KEYS } from './routes';

/**
 * Single source of truth mapping each vault topic (by its vaultState pageKey)
 * to a Heroicon. Consumed by the vault sidebar, the shared EmptyState, and the
 * dashboard cards so all three render the same icon for a given topic and can
 * never drift apart (MCV-1495).
 *
 * Keyed by pageKey (not the route emoji) so topics that share a page — DEA and
 * CDS both live under `licenses`, Education and Training both under `academics`
 * — resolve to one icon automatically.
 */
export const VAULT_TOPIC_ICONS: Record<string, IconType> = {
  [PROVIDER_PAGE_KEYS.dashboard]: HomeIcon,
  [PROVIDER_PAGE_KEYS.personalProfile]: UserIcon,
  [PROVIDER_PAGE_KEYS.aoi]: TagIcon,
  [PROVIDER_PAGE_KEYS.settings]: Cog6ToothIcon,
  [PROVIDER_PAGE_KEYS.employment]: BriefcaseIcon,
  [PROVIDER_PAGE_KEYS.academics]: AcademicCapIcon,
  [PROVIDER_PAGE_KEYS.medicalLicense]: IdentificationIcon,
  [PROVIDER_PAGE_KEYS.licenses]: ShieldCheckIcon,
  [PROVIDER_PAGE_KEYS.boardCertifications]: CheckBadgeIcon,
  [PROVIDER_PAGE_KEYS.certifications]: DocumentCheckIcon,
  [PROVIDER_PAGE_KEYS.cmeTraining]: BookOpenIcon,
  [PROVIDER_PAGE_KEYS.privileges]: ClipboardDocumentCheckIcon,
  [PROVIDER_PAGE_KEYS.malpractice]: ScaleIcon,
  [PROVIDER_PAGE_KEYS.attestations]: PencilSquareIcon,
  [PROVIDER_PAGE_KEYS.peerReferences]: UserGroupIcon,
  [PROVIDER_PAGE_KEYS.documents]: DocumentIcon,
};

/**
 * Resolve a vault topic's icon by pageKey. Accepts an optional value with an
 * optional `#tab` suffix (dashboard navigateTo strings carry one, e.g.
 * `licenses#dea`) and strips it before lookup. Returns undefined for unknown
 * keys so callers can fall back to a default.
 */
export const getVaultTopicIcon = (pageKey?: string): IconType | undefined => {
  if (!pageKey) return undefined;
  return VAULT_TOPIC_ICONS[pageKey.split('#')[0]];
};
